import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero } from "@/components/CTA";
import { Seo } from "@/components/Seo";

const channels = [
  { icon: Mail, label: "Email", value: "care@hewto.ai", href: "mailto:care@hewto.ai", testid: "contactus-email" },
  { icon: MapPin, label: "Office", value: "United States", href: null, testid: "contactus-office" },
  { icon: Clock, label: "Support hours", value: "Mon–Fri, 9am–6pm ET", href: null, testid: "contactus-hours" },
];

const ContactUs = () => (
  <PageTransition>
    <Seo
      title="Contact Us | Hewto.ai"
      description="Get in touch with Hewto.ai — questions on our intelligent document processing platform for HCFA, UB-04, and dental claims, plus support, partnerships, and press."
      keywords="contact hewto.ai, healthcare document ai support, medical claims ocr contact"
      canonical="https://www.hewto.ai/contact-us"
    />
    <PageHero
      eyebrow="Contact Us"
      title="We'd love to hear from you"
      subtitle="Questions about the platform, support, or partnerships? Reach out through any channel below — or book a demo with our sales team."
    />

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {channels.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                <c.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 text-sm font-semibold uppercase tracking-wider text-slate-400">
                {c.label}
              </h3>
              {c.href ? (
                <a
                  href={c.href}
                  data-testid={c.testid}
                  className="mt-1 block text-lg font-semibold text-slate-900 transition-colors hover:text-brand"
                >
                  {c.value}
                </a>
              ) : (
                <p data-testid={c.testid} className="mt-1 text-lg font-semibold text-slate-900">
                  {c.value}
                </p>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">
              How we can help
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Whether you&apos;re a payer, provider, clearinghouse, or RCM team, our team is here
              to help you understand how Hewto.ai can automate your document workflows. We work
              with organizations processing anywhere from a few hundred claims a month to millions,
              and we&apos;re happy to talk through what a rollout on your specific documents would
              look like.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              For product questions and evaluations, the fastest path is a live demo — bring a
              handful of your own HCFA, UB-04, or dental claim samples and we&apos;ll run them
              through the platform on the call. For support, technical questions, or partnership
              inquiries, email us at care@hewto.ai and a member of our team will respond within one
              business day.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              Prefer to write in first? Send us a note describing what you&apos;re trying to
              automate, your rough document volume, and any deadlines we should know about. We
              read every message and route it to the right person on our team.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-900">What to expect:</span> we typically
              respond within one business day (Monday–Friday, 9am–6pm ET). For urgent production
              support, please note that in the subject line and we&apos;ll fast-track your request.
              Every inquiry gets a human reply — no auto-responders, no chatbots.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              Who to reach
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-700">
              <li><span className="font-semibold">Sales &amp; demos:</span> <Link to="/contact-sales" className="text-brand hover:underline">Contact Sales</Link></li>
              <li><span className="font-semibold">Support &amp; general:</span> care@hewto.ai</li>
              <li><span className="font-semibold">Billing:</span> billing@hewto.ai</li>
              <li><span className="font-semibold">Privacy:</span> privacy@hewto.ai</li>
              <li><span className="font-semibold">Legal:</span> legal@hewto.ai</li>
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-12 flex flex-col items-center justify-between gap-6 rounded-3xl border border-slate-200 bg-slate-50 p-10 sm:flex-row">
          <div>
            <h3 className="text-2xl font-bold text-slate-900">Looking to evaluate Hewto.ai?</h3>
            <p className="mt-2 text-slate-600">Book a tailored demo and see it run on your documents.</p>
          </div>
          <Button
            asChild
            data-testid="contactus-book-demo-btn"
            className="rounded-full bg-brand px-6 py-6 text-base font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-600"
          >
            <Link to="/contact-sales">
              Contact Sales <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </Reveal>
    </section>
  </PageTransition>
);

export default ContactUs;
